import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import SearchBox from '../../components/SearchBox/SearchBox';
import ProductCard from '../../components/ProductCard/ProductCard';
import { useAppContext } from '../../contexts/AppContext';
import { fetchHomeDashboard } from '../../services/homeDashboardService';
import { useSearchParams } from 'react-router';

function mergeProductsByProduct(items) {
  const groupedProducts = new Map();

  items.forEach((item) => {
    const productKey = item.product_id || item.id || `${item.name}-${item.image}`;
    const existingItem = groupedProducts.get(productKey);
    const variantLabel = item.size || item.variant_name || item.weight || '';

    if (!existingItem) {
      groupedProducts.set(productKey, {
        ...item,
        id: item.product_id || item.id,
        size: item.size || item.variant_name || '',
        variants: variantLabel ? [variantLabel] : [],
      });
      return;
    }

    const existingPrice = Number(existingItem.price || 0);
    const nextPrice = Number(item.price || 0);

    if (nextPrice > 0 && (existingPrice === 0 || nextPrice < existingPrice)) {
      existingItem.price = item.price;
    }

    if (!existingItem.size && variantLabel) {
      existingItem.size = variantLabel;
    }

    if (variantLabel && !existingItem.variants.includes(variantLabel)) {
      existingItem.variants.push(variantLabel);
    }
  });

  return Array.from(groupedProducts.values());
}

export default function Search() {
  const { user } = useAppContext();
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const userId = useMemo(
    () => user?.user_id ?? user?.id ?? user?.customer_id ?? '',
    [user]
  );

  useEffect(() => {
    let isMounted = true;

    const loadSearchProducts = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await fetchHomeDashboard({ userId });

        if (!isMounted) {
          return;
        }

        const mergedProducts = mergeProductsByProduct([
          ...response.bestSelling,
          ...response.newArrivals,
          ...response.recommended,
          ...response.trending,
        ]);

        setAllProducts(mergedProducts);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadError(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSearchProducts();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const filteredProducts = useMemo(() => {
    if (!query) {
      return allProducts;
    }

    const normalizedQuery = query.toLowerCase();

    return allProducts.filter((product) => {
      const variants = Array.isArray(product.variants) ? product.variants.join(' ') : '';

      return [product.name, product.description, product.size, variants]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
  }, [allProducts, query]);

  return (
    <div>
      <SearchBox />

      {loadError ? (
        <Alert severity='error' sx={{ mx: 2, mt: 2 }}>
          {loadError.message}
        </Alert>
      ) : null}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredProducts.length ? (
        <ProductCard
          products={filteredProducts}
          title={query ? `Search results for "${query}"` : 'All products'}
        />
      ) : (
        <Typography sx={{ px: 2, py: 4, color: 'text.secondary' }}>
          No products matched "{query}".
        </Typography>
      )}
    </div>
  );
}
